import { motion } from 'framer-motion';
import { Phone, Play, User } from 'lucide-react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

// ── Deploy appscript/video_tracking.gs as its own Web App (see its header
// comment for steps), then replace this placeholder with the resulting /exec
// URL. It logs viewer name + phone + highest % of a gated video watched.
export const VIDEO_TRACKING_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwuqSDfnP51EEWcLmNjuBfhMgEWviuujaMpOeR3sGBUFak0QMLTUsWRWvuy9FAVTaI4/exec';

// ─── Shared viewer identity (name+phone-gate all videos on a page as one) ──
const VIEWER_NAME_KEY = 'vt_viewer_name';
const VIEWER_PHONE_KEY = 'vt_viewer_phone';

type ViewerCtxValue = {
  name: string;
  phone: string;
  unlocked: boolean;
  submit: (name: string, phone: string) => void;
};
const ViewerContext = createContext<ViewerCtxValue | null>(null);

export const ViewerProvider = ({ children }: { children: React.ReactNode }) => {
  const [name, setName] = useState(() => sessionStorage.getItem(VIEWER_NAME_KEY) || '');
  const [phone, setPhone] = useState(() => sessionStorage.getItem(VIEWER_PHONE_KEY) || '');
  const [unlocked, setUnlocked] = useState(() => !!(sessionStorage.getItem(VIEWER_NAME_KEY) && sessionStorage.getItem(VIEWER_PHONE_KEY)));

  const submit = (n: string, p: string) => {
    sessionStorage.setItem(VIEWER_NAME_KEY, n);
    sessionStorage.setItem(VIEWER_PHONE_KEY, p);
    setName(n);
    setPhone(p);
    setUnlocked(true);
  };

  return (
    <ViewerContext.Provider value={{ name, phone, unlocked, submit }}>
      {children}
    </ViewerContext.Provider>
  );
};

export const useViewer = () => {
  const ctx = useContext(ViewerContext);
  if (!ctx) throw new Error('useViewer must be used within a ViewerProvider');
  return ctx;
};

// ─── Gated video (name+phone unlock, shared across the whole page, tracks % watched) ──
export const GatedVideo = ({
  src, sourceLabel, playLabel = 'Watch the Video', className = '',
}: { src: string; sourceLabel: string; playLabel?: string; className?: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { name, phone, unlocked, submit } = useViewer();
  const [formName, setFormName] = useState(name);
  const [formPhone, setFormPhone] = useState(phone);
  const [nameError, setNameError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const maxPercentRef = useRef(0);
  const maxWatchedSecondsRef = useRef(0);
  const totalDurationRef = useRef(0);
  const lastSentRef = useRef(0);

  const sendProgress = (useBeacon = false) => {
    const percent = Math.min(100, Math.round(maxPercentRef.current));
    if (!unlocked || percent <= lastSentRef.current) return;
    lastSentRef.current = percent;
    const payload = JSON.stringify({
      name, phone,
      percentWatched: percent,
      secondsWatched: Math.round(maxWatchedSecondsRef.current),
      totalSeconds: Math.round(totalDurationRef.current),
      timestamp: new Date().toISOString(),
      source: sourceLabel,
    });
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(VIDEO_TRACKING_SCRIPT_URL, new Blob([payload], { type: 'application/json' }));
    } else {
      fetch(VIDEO_TRACKING_SCRIPT_URL, {
        method: 'POST', mode: 'no-cors', headers: { 'Content-Type': 'application/json' }, body: payload,
      }).catch(() => {});
    }
  };

  useEffect(() => {
    const handleHide = () => sendProgress(true);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') handleHide(); });
    window.addEventListener('pagehide', handleHide);
    return () => {
      handleHide();
      window.removeEventListener('pagehide', handleHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked, name, phone]);

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    totalDurationRef.current = v.duration;
    const pct = (v.currentTime / v.duration) * 100;
    if (pct > maxPercentRef.current) {
      maxPercentRef.current = pct;
      maxWatchedSecondsRef.current = v.currentTime;
    }
    // Log at 25% milestones so long views aren't lost if the tab is closed abruptly.
    if (Math.floor(pct / 25) > Math.floor(lastSentRef.current / 25)) sendProgress();
  };

  const handleEnded = () => {
    maxPercentRef.current = 100;
    maxWatchedSecondsRef.current = totalDurationRef.current;
    sendProgress();
  };
  const handlePause = () => sendProgress();

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validName = formName.trim().length >= 2;
    const validPhone = /^[0-9]{10}$/.test(formPhone.trim());
    setNameError(!validName);
    setPhoneError(!validPhone);
    if (!validName || !validPhone) return;
    submit(formName.trim(), formPhone.trim());
    setTimeout(() => videoRef.current?.play(), 50);
  };

  return (
    <div className={`relative rounded-2xl overflow-hidden border border-white/12 bg-black ${!unlocked ? 'min-h-[440px]' : ''} ${className}`}>
      <video
        ref={videoRef}
        src={src}
        controls={unlocked}
        playsInline
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onPause={handlePause}
        onEnded={handleEnded}
        className="w-full aspect-video bg-black"
      />
      {!unlocked && (
        <div className="absolute inset-0 bg-primary/95 backdrop-blur-sm flex items-center justify-center overflow-y-auto p-6">
          <form onSubmit={handleFormSubmit} className="w-full max-w-sm text-center my-auto">
            <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center mx-auto mb-4">
              <Play className="text-primary" size={22} fill="currentColor" />
            </div>
            <h3 className="text-white font-bold text-lg mb-1.5">Enter your details to watch</h3>
            <p className="text-[#c2b6d4] text-xs mb-4 leading-relaxed">
              So a mentor can follow up if you have questions after.
            </p>
            <div className="relative mb-3">
              <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#756890]" />
              <input
                type="text" value={formName} onChange={e => { setFormName(e.target.value); setNameError(false); }}
                placeholder="Your name"
                className={`w-full pl-9 pr-3 py-3 rounded-lg bg-white/5 border text-white text-sm placeholder-white/30 outline-none transition-colors ${nameError ? 'border-orange' : 'border-white/15 focus:border-accent'}`}
              />
            </div>
            {nameError && <p className="text-orange text-xs mb-3 text-left">Please enter your name.</p>}
            <div className="relative">
              <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#756890]" />
              <input
                type="tel" value={formPhone} onChange={e => { setFormPhone(e.target.value); setPhoneError(false); }}
                placeholder="10-digit mobile number"
                className={`w-full pl-9 pr-3 py-3 rounded-lg bg-white/5 border text-white text-sm placeholder-white/30 outline-none transition-colors ${phoneError ? 'border-orange' : 'border-white/15 focus:border-accent'}`}
              />
            </div>
            {phoneError && <p className="text-orange text-xs mt-1.5 text-left">Enter a valid 10-digit number.</p>}
            <motion.button
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit"
              className="mt-4 w-full py-3.5 rounded-lg bg-accent text-primary font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-accent/30"
            >
              <Play size={15} fill="currentColor" /> {playLabel}
            </motion.button>
          </form>
        </div>
      )}
    </div>
  );
};
